import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAdminStore } from '@/stores/adminStore';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Spinner } from '@/components/common/Spinner';
import * as adminApi from '@/api/admin';
import type { AdminEntry, AdminRole } from '@/types/admin';

export default function AdminAdminsPage() {
  const { admin, clearAdmin } = useAdminStore();
  const [admins, setAdmins] = useState<AdminEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<AdminRole>('admin');
  const [isSaving, setIsSaving] = useState(false);

  const loadAdmins = useCallback(async () => {
    try {
      const response = await adminApi.listAdmins();
      setAdmins(response.admins);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load admins');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const handleAdd = async () => {
    if (!newEmail.trim()) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await adminApi.addAdmin(newEmail.trim(), newRole);
      setSuccess(response.message);
      setIsAdding(false);
      setNewEmail('');
      setNewRole('admin');
      loadAdmins();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add admin');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (!confirm(`Remove ${email} as admin?`)) return;
    setError(null);
    setSuccess(null);
    try {
      const response = await adminApi.removeAdmin(email);
      setSuccess(response.message);
      loadAdmins();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove admin');
    }
  };

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      clearAdmin();
    }
  };

  if (isLoading) {
    return <Spinner size="lg" centered />;
  }

  return (
    <div className="min-h-screen bg-md-cream py-12">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-md-graphite">
              Admin Management
            </h1>
            <p className="text-sm text-md-slate mt-1">
              Signed in as {admin?.email} (super_admin)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="text-sm font-medium text-md-sky-strong hover:text-md-graphite transition-colors"
            >
              ← Admin
            </Link>
            <Button variant="secondary" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-md-error-bg border border-md-error text-md-error p-4 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-md-success-bg border border-md-success text-md-success p-4 rounded mb-6">
            {success}
          </div>
        )}

        <Card>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-md-graphite">Admins</h2>
            {!isAdding && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsAdding(true)}
              >
                Add Admin
              </Button>
            )}
          </div>

          {isAdding && (
            <div className="border-2 border-md-sky rounded p-4 mb-6">
              <div className="space-y-4">
                <Input
                  label="Email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="user@tubi.tv"
                />
                <div className="w-full">
                  <label className="block text-sm font-medium text-md-ink mb-2">
                    Role
                  </label>
                  <select
                    value={newRole}
                    onChange={(e) =>
                      setNewRole(e.target.value as AdminRole)
                    }
                    className="w-full px-4 py-2 border-2 border-md-graphite rounded bg-md-fog text-md-ink focus:outline-none focus:border-md-sky-strong"
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAdd}
                  loading={isSaving}
                  disabled={!newEmail.trim()}
                >
                  Add
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsAdding(false);
                    setNewEmail('');
                    setNewRole('admin');
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {admins.map((a) => (
              <div
                key={a.email}
                className="flex items-center justify-between py-3 border-b border-md-fog last:border-b-0"
              >
                <div>
                  <p className="font-bold text-md-graphite">{a.email}</p>
                  <p className="text-sm text-md-slate">
                    {a.role === 'super_admin' ? 'Super Admin' : 'Admin'} —
                    added by {a.addedBy} on{' '}
                    {new Date(a.createdAt).toLocaleDateString()}
                  </p>
                </div>
                {a.email !== admin?.email && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemove(a.email)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ))}
            {admins.length === 0 && (
              <p className="text-md-slate text-center py-4">
                No admins configured.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
